/**
 * RulesPage (FE-4, PHASE1 §7.1). Lists rules in priority order with inline
 * reorder (▲/▼ → POST /rules/reorder), edit, delete, and apply-to-existing.
 * Creating and editing go through RuleFormDialog (which embeds the dry-run
 * preview). German-only copy.
 */
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal } from '../components/ui/Modal.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { RuleFormDialog } from './rules/RuleFormDialog.tsx';
import { useToast } from '../context/ToastContext.tsx';
import { useBucketMap } from '../lib/hooks/useBuckets.ts';
import {
  actionSummary,
  conditionSummary,
  useApplyRule,
  useDeleteRule,
  useReorderRules,
  useRules,
} from '../lib/hooks/useRules.ts';
import type { RuleOut } from '../lib/types.ts';

export default function RulesPage() {
  const { toast } = useToast();
  const rulesQuery = useRules();
  const bucketMap = useBucketMap();
  const reorderRules = useReorderRules();
  const deleteRule = useDeleteRule();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RuleOut | null>(null);
  const [deleting, setDeleting] = useState<RuleOut | null>(null);
  const [applying, setApplying] = useState<RuleOut | null>(null);

  const rules = useMemo(
    () => [...(rulesQuery.data ?? [])].sort((a, b) => a.priority - b.priority || a.id - b.id),
    [rulesQuery.data],
  );

  const bucketName = (id: number) => bucketMap.get(id)?.name;

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    reorderRules.mutate(
      { ordered_ids: next.map((r) => r.id) },
      { onError: (err) => toast(err.message, { variant: 'error' }) },
    );
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(rule: RuleOut) {
    setEditing(rule);
    setFormOpen(true);
  }

  function confirmDelete() {
    if (!deleting) return;
    const rule = deleting;
    deleteRule.mutate(rule.id, {
      onSuccess: () => {
        toast(`Regel „${rule.name}“ gelöscht.`, { variant: 'success' });
        setDeleting(null);
      },
      onError: (err) => toast(err.message, { variant: 'error' }),
    });
  }

  return (
    <div>
      <PageHeader
        title="Regeln"
        subtitle={rules.length > 0 ? `${rules.length} Regeln` : undefined}
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Neue Regel
          </Button>
        }
      />

      <div className="w-full px-4 py-6 sm:px-6">
        {rulesQuery.isPending ? (
          <SkeletonList rows={4} />
        ) : rulesQuery.isError ? (
          <ErrorState error={rulesQuery.error} onRetry={() => void rulesQuery.refetch()} />
        ) : rules.length === 0 ? (
          <EmptyState
            title="Noch keine Regeln"
            description="Regeln kategorisieren neue Umsätze automatisch beim Import."
            action={
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Erste Regel anlegen
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {rules.map((rule, index) => (
              <li key={rule.id} className="py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-slate-900 dark:text-slate-100">
                        {rule.name}
                      </span>
                      {!rule.active ? (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          Inaktiv
                        </span>
                      ) : null}
                      {rule.stop_processing ? (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          Stopp
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {conditionSummary(rule)}
                    </p>
                    <p className="text-sm font-medium text-sky-600 dark:text-sky-400">
                      {actionSummary(rule, bucketName)}
                    </p>
                    <p className="text-xs text-slate-400 tabular-nums dark:text-slate-500">
                      Priorität {rule.priority}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Nach oben"
                        disabled={index === 0 || reorderRules.isPending}
                        onClick={() => move(index, -1)}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Nach unten"
                        disabled={index === rules.length - 1 || reorderRules.isPending}
                        onClick={() => move(index, 1)}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Auf Bestehende anwenden"
                        onClick={() => setApplying(rule)}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Bearbeiten"
                        onClick={() => openEdit(rule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Löschen"
                        onClick={() => setDeleting(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <RuleFormDialog open={formOpen} onOpenChange={setFormOpen} rule={editing} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Regel löschen"
        message={
          deleting
            ? `„${deleting.name}“ wirklich löschen? Bereits gesetzte Kategorien bleiben erhalten.`
            : ''
        }
        confirmLabel="Löschen"
        danger
        loading={deleteRule.isPending}
        onConfirm={confirmDelete}
      />

      <ApplyRuleModal rule={applying} onClose={() => setApplying(null)} />
    </div>
  );
}

/** Apply-to-existing dialog: one option (only-uncategorized), reports results. */
function ApplyRuleModal({ rule, onClose }: { rule: RuleOut | null; onClose: () => void }) {
  const { toast } = useToast();
  const applyRule = useApplyRule();
  const [onlyUncategorized, setOnlyUncategorized] = useState(true);

  function apply() {
    if (!rule) return;
    applyRule.mutate(
      { id: rule.id, body: { only_uncategorized: onlyUncategorized } },
      {
        onSuccess: (result) => {
          toast(`${result.applied} angewendet, ${result.skipped_user} vom Nutzer geschützt.`, {
            variant: 'success',
          });
          onClose();
        },
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  return (
    <Modal
      open={rule !== null}
      onOpenChange={(open) => !open && onClose()}
      title="Auf bestehende Umsätze anwenden"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={applyRule.isPending}>
            Abbrechen
          </Button>
          <Button onClick={apply} disabled={applyRule.isPending}>
            {applyRule.isPending ? 'Wende an …' : 'Anwenden'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Die Regel {rule ? `„${rule.name}“ ` : ''}wird auf bereits erfasste Transaktionen
          angewendet. Vom Nutzer kategorisierte Umsätze bleiben unberührt.
        </p>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
            checked={onlyUncategorized}
            onChange={(e) => setOnlyUncategorized(e.target.checked)}
          />
          Nur unkategorisierte Umsätze
        </label>
      </div>
    </Modal>
  );
}
